import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from '../utils/axios';
import { DollarSign, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';

export default function BudgetDashboard() {
  const { discoveryId } = useParams();
  const [budget, setBudget] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (discoveryId) {
      fetchBudgetData();
    }
  }, [discoveryId]);

  const fetchBudgetData = async () => {
    try {
      const [budgetRes, statsRes] = await Promise.all([
        apiClient.get(`/budget/${discoveryId}`),
        apiClient.get(`/budget/${discoveryId}/statistics`)
      ]);

      setBudget(budgetRes.data);
      setStatistics(statsRes.data.statistics);
    } catch (error) {
      console.error('Error fetching budget data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading budget data...</p>
      </div>
    );
  }

  if (!budget) {
    return (
      <div className="p-8 text-center text-gray-500">
        No budget data available. Please select a discovery campaign.
      </div>
    );
  }

  const { budget: budgetData, burnRate } = budget;
  const percentage = budgetData.percentage || 0;
  const isOverBudget = budgetData.limit && budgetData.spend > budgetData.limit;
  const isNearBudget = budgetData.limit && percentage > 80;

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <DollarSign size={32} className="text-purple-600" />
          Budget Dashboard
        </h1>
        <p className="mt-2 text-gray-600">Monitor and manage discovery campaign budget</p>
      </div>

      {/* Budget Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Current Spend</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                ${budgetData.spend?.toFixed(2) || '0.00'}
              </p>
            </div>
            <DollarSign size={32} className="text-purple-600 opacity-50" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Budget Limit</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {budgetData.limit ? `$${budgetData.limit.toFixed(2)}` : 'Unlimited'}
              </p>
            </div>
            <TrendingUp size={32} className="text-blue-600 opacity-50" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Remaining</p>
              <p className={`text-2xl font-bold mt-1 ${
                isOverBudget ? 'text-red-600' :
                isNearBudget ? 'text-yellow-600' :
                'text-gray-900'
              }`}>
                {budgetData.remaining !== null ? `$${budgetData.remaining.toFixed(2)}` : 'N/A'}
              </p>
            </div>
            {isOverBudget ? (
              <AlertCircle size={32} className="text-red-600 opacity-50" />
            ) : isNearBudget ? (
              <AlertCircle size={32} className="text-yellow-600 opacity-50" />
            ) : (
              <CheckCircle size={32} className="text-green-600 opacity-50" />
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Burn Rate</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                ${burnRate?.dailyAverage?.toFixed(2) || '0.00'}/day
              </p>
              {burnRate?.estimatedDaysRemaining && (
                <p className="text-xs text-gray-500 mt-1">
                  ~{burnRate.estimatedDaysRemaining} days remaining
                </p>
              )}
            </div>
            <TrendingUp size={32} className="text-green-600 opacity-50" />
          </div>
        </div>
      </div>

      {/* Budget Progress */}
      {budgetData.limit && (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-semibold">Budget Usage</h2>
            <span className={`text-sm font-medium ${
              isOverBudget ? 'text-red-600' :
              isNearBudget ? 'text-yellow-600' :
              'text-gray-600'
            }`}>
              {percentage.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className={`h-4 rounded-full ${
                isOverBudget ? 'bg-red-600' :
                isNearBudget ? 'bg-yellow-600' :
                'bg-purple-600'
              }`}
              style={{ width: `${Math.min(100, percentage)}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Statistics */}
      {statistics && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Cost Breakdown</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-gray-600">Total Cost</span>
                <span className="text-sm font-medium">${statistics.totalCost?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-gray-600">Total Tokens</span>
                <span className="text-sm font-medium">{statistics.totalTokens?.toLocaleString() || '0'}</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-gray-600">Operations</span>
                <span className="text-sm font-medium">{statistics.operationCount || 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


